import { useState, type ReactNode } from 'react';
import { ApiError } from '../api.ts';
import { t } from '../i18n/index.ts';
import { setAccessReason, type ReasonChallenge } from '../lib/evidence.ts';
import {
  Button,
  Empty,
  Field,
  Finding,
  PageTitle,
  Section,
  Select,
  Spinner,
  sentence,
} from '../ui.tsx';

/**
 * Shared chrome for the evidence-workspace surfaces (Cases, Disclosure,
 * Redaction). The Evidence module (`screens/evidence.tsx`) owns the same three
 * seam states — unconfigured (501), not-enabled (403), reason-for-access (428)
 * — and these surfaces sit behind the identical seam, so they answer with the
 * identical honest sentences rather than a raw error. Kept here so the wording
 * is written once and the three new screens stay consistent with Evidence.
 *
 * The one deliberate difference from Evidence's own onboarding panel: turning
 * the module *on* lives on the Evidence tab (it is one act, with the admin
 * gate and the PUT), so a not-enabled workspace surface points there rather
 * than duplicating the enable control three times.
 */

/* ------------------------------------------------------------ format helpers */

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function shortId(id: string, n = 8): string {
  return id.length > n ? `${id.slice(0, n)}…` : id;
}

/** Download an object as pretty-printed JSON, the same shape Evidence uses. */
export function download(name: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/* --------------------------------------------------------------- the prompt */

/**
 * The §1.3 reason-for-access prompt — a stated reason from the service's own
 * pick-list, recorded in the audit trail beside the access. This is the single
 * shared copy: the Evidence module imports it from here too, so every surface
 * behind the identical seam asks in identical words rather than drifting apart.
 */
export function ReasonPrompt({
  challenge,
  onStated,
}: {
  challenge: ReasonChallenge;
  onStated: () => void;
}) {
  const [cls, setCls] = useState(challenge.classes[0] ?? 'investigation');
  const [text, setText] = useState('');
  return (
    <Section title={t('reason.title')}>
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">{t('reason.intro')}</p>
      <div className="flex max-w-[560px] flex-col gap-4">
        <Select
          label={t('reason.field.reason')}
          value={cls}
          onChange={setCls}
          options={challenge.classes.map((c) => ({
            value: c,
            label: sentence(c.replaceAll('_', ' ')),
          }))}
        />
        <Field
          label={t('reason.field.detail')}
          value={text}
          onChange={setText}
          placeholder={t('reason.field.detail.placeholder')}
          hint={t('reason.field.detail.hint')}
        />
        <div>
          <Button
            variant="primary"
            onClick={() => {
              setAccessReason(cls, text.trim() || undefined);
              onStated();
            }}
          >
            {t('reason.continue')}
          </Button>
        </div>
      </div>
    </Section>
  );
}

/* ----------------------------------------------------------------- the gate */

/**
 * Wrap a workspace surface. It renders the right sentence for whichever seam
 * state it finds and only renders `children` once the surface is actually
 * reachable. `title`/`sub` head every state so the page never flickers its
 * identity.
 */
export function EvidenceGate({
  title,
  sub,
  err,
  loading,
  challenge,
  onStated,
  onGoToEvidence,
  children,
}: {
  title: string;
  sub: string;
  err: unknown;
  loading: boolean;
  challenge: ReasonChallenge | null;
  onStated: () => void;
  /** Navigate to the Evidence tab, where the module is switched on. */
  onGoToEvidence?: () => void;
  children: ReactNode;
}) {
  const head = <PageTitle sub={sub}>{title}</PageTitle>;

  if (err instanceof ApiError && err.status === 501) {
    return (
      <>
        {head}
        <Empty>{t('gate.unconfigured')}</Empty>
      </>
    );
  }

  if (err instanceof ApiError && err.status === 403 && /not enabled/.test(err.message)) {
    return (
      <>
        {head}
        <Section title={t('gate.notEnabled.title')}>
          <p className="mb-4 max-w-prose text-[14px]">{t('gate.notEnabled.body')}</p>
          {onGoToEvidence ? (
            <Button variant="primary" onClick={onGoToEvidence}>
              {t('gate.notEnabled.cta')}
            </Button>
          ) : null}
        </Section>
      </>
    );
  }

  // A role with no standing in the evidence service (403 forbidden), from
  // either the token exchange or a workspace route's role gate.
  const status =
    err instanceof ApiError
      ? err.status
      : err && typeof err === 'object' && 'status' in err
        ? (err as { status?: number }).status
        : undefined;
  if (status === 403) {
    return (
      <>
        {head}
        <Empty>{t('gate.forbidden')}</Empty>
      </>
    );
  }

  if (challenge !== null) {
    return (
      <>
        {head}
        <ReasonPrompt challenge={challenge} onStated={onStated} />
      </>
    );
  }

  if (loading) {
    return (
      <>
        {head}
        <Spinner />
      </>
    );
  }

  if (err) {
    return (
      <>
        {head}
        <Section title={t('gate.error.title')}>
          <p className="mb-3 max-w-prose text-[14px]">{t('gate.error.body')}</p>
          <Finding tone="alert">{(err as Error).message}</Finding>
        </Section>
      </>
    );
  }

  return (
    <>
      {head}
      {children}
    </>
  );
}
